<template>
  <ClientOnly>
    <div class="relative flex flex-1 flex-col">
      <label
        class="mb-1.5 inline-block font-sans text-base text-black lg:mb-3 lg:text-lg lg:leading-snug"
        :class="styleLabel"
      >
        {{ label }}
        <span v-if="required">*</span>
      </label>
      <div class="relative">
        <VueDatePicker
          ref="datepicker"
          v-model="value"
          :action-row="{ showSelect: false, showCancel: false, showNow: false, showPreview: false }"
          :hide-navigation="['time']"
          :flow="flow"
          :start-date="startDate"
          :max-date="new Date()"
          @date-update="handleDate"
          @cleared="handleClear"
          :locale="lang"
          :format="format"
          :input-class-name="
            twMerge(
              'h-[47px] lg:h-[60px] flex-1 !rounded-md border-transparent leading-none focus:border-transparent focus:ring-0 lg:!text-xl lg:!leading-none py-3 lg:py-4',
              styleInput,
              v$?.$invalid && v$?.$dirty && '!border-pink-act focus:!border-pink-act',
            )
          "
          :class="`datepicker-${theme}`"
          :placeholder="placeholder"
          :teleport="teleport"
        />

        <div
          v-if="v$?.$invalid && v$?.$dirty && v$?.$errors?.length > 0"
          class="absolute mt-0.5 flex w-full flex-col items-start rounded px-1 py-0 pr-3 font-sans text-sm text-pink-act"
          :class="styleError"
          role="alert"
        >
          <span v-for="err in v$.$errors" :key="err.$uid">{{ err.$message }}</span>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
// https://vue3datepicker.com/installation/
import { computed, ref, onMounted, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Validation } from '@vuelidate/core'

import { useVModel } from '@vueuse/core'

import type { ModelValue, DatePickerInstance } from '@vuepic/vue-datepicker'
import { format as formatDate } from 'date-fns'
import { twMerge } from 'tailwind-merge'
import { ensureDatePickerCss } from '../../libs/datePickerCss'

import { THEMES } from 'refresh-ui'
import type { SiteLang } from '../../types/env'

const VueDatePicker = defineAsyncComponent(() =>
  import('@vuepic/vue-datepicker').then(module => module.default),
)

const config = useRuntimeConfig()
const route = useRoute()

const lang = (route.query.lang as SiteLang) || config.public.SITE_LANG

interface Props {
  date: ModelValue
  label?: string
  styleInput?: string
  styleLabel?: string
  required?: boolean
  styleError?: string
  format?: string
  v$?: Validation
  flow?: ('month' | 'year' | 'calendar' | 'time' | 'minutes' | 'hours' | 'seconds')[]
  theme?: THEMES
  startYear?: number
  placeholder?: string
  teleport?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  format: 'yyyy-MM-dd',
  //@ts-ignore
  flow: ['year', 'month', 'calendar'],
  theme: THEMES.DEFAULT,
  startYear: 1990,
})

const flow = ref(props.flow)
const datepicker = ref<DatePickerInstance>(null)
const startDate = ref(new Date(props.startYear, 1))

const emit = defineEmits(['update:date'])

const modelDate = useVModel(props, 'date', emit)

const handleDate = (date: Date) => {
  modelDate.value = formatDate(date, props.format)
  if (datepicker.value) {
    ;(datepicker.value as any).closeMenu?.()
  }
}

const handleClear = () => {
  modelDate.value = ''
}

const value = computed(() => modelDate.value)

onMounted(async () => {
  ensureDatePickerCss()
})
</script>

<style>
.datepicker-djurens_ratt .dp__theme_light,
.datepicker-djurens_ratt.dp__theme_light,
.datepicker-default .dp__theme_light,
.datepicker-default.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #00b4d2;
  --dp-primary-disabled-color: #6bacea;
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #d8d8d8;
  --dp-border-color: #0088a4;
  --dp-menu-border-color: #0088a4;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
  --dp-cell-border-radius: 8px;

  --dp-font-size: 15px;

  --dp-menu-padding: 8px 16px 4px 16px;
}

.datepicker-rdk.dp__theme_light,
.datepicker-rdk .dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #7a54c3;
  --dp-primary-disabled-color: #6bacea;
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #d8d8d8;
  --dp-border-color: #f6ae8b;
  --dp-menu-border-color: #f6ae8b;
  --dp-border-color-hover: #f6ae8b;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
  --dp-cell-border-radius: 8px;

  --dp-font-size: 15px;

  --dp-menu-padding: 8px 16px 4px 16px;
  --dp-font-family: 'Avenir LT Std';
}

.dp__menu {
  border-radius: 16px;
  box-shadow: 0px 0px 15px 0px rgba(0, 180, 210, 0.23);
  overflow: hidden;
}
.dp__today {
  border: transparent;
  font-weight: bold;
}
.dp__active_date {
  font-weight: bold;
}
.dp__calendar_header_separator {
  display: none;
}
.dp__calendar_header {
  font-size: 11px;
  font-weight: 600;
  color: #929292;
}

.dp__calendar_header_item {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dp__cell_disabled {
  text-decoration: line-through;
  color: #929292;
}

.dp__pointer.dp__cell_offset {
  color: #d8d8d8;
}
.dp__pointer.dp__active_date {
  color: #fff;
}

.dp__input {
  border: 1px solid var(--dp-border-color) !important;
}
</style>
